import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as reportService from 'services/reports/reportService'
import type {
	ReportFormat,
	ReportRow,
	ReportScope,
	TemplateDescriptor,
} from 'services/reports/reportService'
import { sourceKey } from 'services/reports/reportService'

const SLICE_NAME = 'reports'

function getToken(getState: any): string {
	const {
		userLogin: { userInfo },
	} = getState()
	return userInfo?.token ?? ''
}

interface ScopedThunkArg {
	scope: ReportScope
	entityId: string
}

interface ReportSliceState {
	// `${scope}:${entityId}` → ReportRow[]. Replaces the pre-MYD-339
	// `byEvaluation` map so Projects / Locations / Portfolios share the
	// same state shape. Use `sourceKey(scope, entityId)` to build the key
	// from React components.
	bySource: Record<string, ReportRow[]>
	// Templates are also scoped — different entity types see different
	// template pickers (e.g. project tabs only see project-snapshot /
	// project-story). Keyed by the source key for the same reason.
	templatesBySource: Record<string, TemplateDescriptor[]>
	templatesLoadedBySource: Record<string, boolean>
	isLoading: boolean
	isGenerating: boolean
	lastGeneratedId: string | null
	error: string | null
}

const initialState: ReportSliceState = {
	bySource: {},
	templatesBySource: {},
	templatesLoadedBySource: {},
	isLoading: false,
	isGenerating: false,
	lastGeneratedId: null,
	error: null,
}

export const fetchReports = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async ({ scope, entityId }: ScopedThunkArg, { getState, rejectWithValue }) => {
		try {
			const reports = await reportService.listReports(scope, entityId, getToken(getState))
			return { key: sourceKey(scope, entityId), reports }
		} catch {
			return rejectWithValue('Failed to load reports')
		}
	},
)

export const listReportTemplates = createAsyncThunk(
	`${SLICE_NAME}/listTemplates`,
	async ({ scope, entityId }: ScopedThunkArg, { getState, rejectWithValue }) => {
		try {
			const templates = await reportService.listTemplates(scope, entityId, getToken(getState))
			return { key: sourceKey(scope, entityId), templates }
		} catch {
			return rejectWithValue('Failed to load report templates')
		}
	},
)

/**
 * Fire the generator. Synchronous on the server today; resolves once the row
 * has transitioned from PENDING to READY (or rejects when the row is FAILED).
 *
 * `templateArgs` (MYD-343) is forwarded to the server's aggregator. For
 * partner-packet templates the dialog populates it with `{ recipientName,
 * recipientCompany?, recipientEmail?, senderNote? }`.
 */
export const generateReport = createAsyncThunk(
	`${SLICE_NAME}/generate`,
	async (
		{
			scope,
			entityId,
			templateKey,
			format,
			templateArgs,
		}: ScopedThunkArg & {
			templateKey: string
			format: ReportFormat
			templateArgs?: Record<string, unknown>
		},
		{ getState, rejectWithValue },
	) => {
		try {
			const row = await reportService.generate(
				scope,
				entityId,
				{ templateKey, format, templateArgs },
				getToken(getState),
			)
			return { key: sourceKey(scope, entityId), row }
		} catch (err: any) {
			const msg =
				err?.response?.data?.message ||
				err?.message ||
				'Report generation failed'
			return rejectWithValue(msg)
		}
	},
)

/**
 * Legacy upload-tracking endpoint (kept so any caller that still uses
 * `dispatch(createReport(...))` keeps working). Only available for the
 * evaluation scope; the new generator-driven flow handles all other scopes.
 */
export const createReport = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (
		{ evalId, payload }: { evalId: string; payload: any },
		{ getState, rejectWithValue },
	) => {
		try {
			const token = getToken(getState)
			const axios = (await import('axios')).default
			await axios.post(`/api/evaluations/${evalId}/reports`, payload, {
				headers: { Authorization: `Bearer ${token}` },
			})
			const reports = await reportService.listReports('evaluation', evalId, token)
			return { key: sourceKey('evaluation', evalId), reports }
		} catch {
			return rejectWithValue('Failed to create report')
		}
	},
)

/**
 * Fetch a generated report's bytes as a Blob. Goes through the slice rather
 * than a component-level useSelector so the auth-token path is the same one
 * every other report endpoint uses (`getToken(getState)`). Doesn't touch
 * slice state — purely a side-effect channel; the Blob returns via the
 * thunk's fulfilled payload.
 *
 * MYD-344: components dispatch this, then build `URL.createObjectURL(blob)`
 * for iframe src / window.open. Avoids the proxy / iframe-navigation quirk
 * AND keeps auth handling consistent with the rest of the slice.
 */
export const fetchReportBlobThunk = createAsyncThunk<
	Blob,
	ScopedThunkArg & { reportId: string },
	{ rejectValue: string }
>(
	`${SLICE_NAME}/fetchBlob`,
	async ({ scope, entityId, reportId }, { getState, rejectWithValue }) => {
		try {
			const blob = await reportService.fetchReportBlob(
				scope,
				entityId,
				reportId,
				getToken(getState),
			)
			return blob
		} catch (err: any) {
			const msg =
				err?.response?.data?.message ||
				err?.message ||
				'Failed to load report file'
			return rejectWithValue(msg)
		}
	},
)

export const deleteReport = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (
		{ scope, entityId, reportId }: ScopedThunkArg & { reportId: string },
		{ getState, rejectWithValue },
	) => {
		try {
			await reportService.deleteReport(scope, entityId, reportId, getToken(getState))
			return { key: sourceKey(scope, entityId), reportId }
		} catch {
			return rejectWithValue('Failed to delete report')
		}
	},
)

const reportSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		clearLastGenerated(state) {
			state.lastGeneratedId = null
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchReports.pending, (state) => {
				state.isLoading = true
			})
			.addCase(fetchReports.fulfilled, (state, action) => {
				state.isLoading = false
				const { key, reports } = action.payload
				state.bySource[key] = reports
			})
			.addCase(fetchReports.rejected, (state, action) => {
				state.isLoading = false
				state.error = (action.payload as string) ?? 'Failed to load reports'
			})

			.addCase(listReportTemplates.fulfilled, (state, action) => {
				const { key, templates } = action.payload
				state.templatesBySource[key] = templates
				state.templatesLoadedBySource[key] = true
			})

			.addCase(generateReport.pending, (state) => {
				state.isGenerating = true
				state.error = null
			})
			.addCase(generateReport.fulfilled, (state, action) => {
				state.isGenerating = false
				const { key, row } = action.payload
				const existing = state.bySource[key] ?? []
				// Newest first — server returns generatedAt sort but we prepend
				// optimistically here so the UI reflects the new row without
				// waiting for the next refetch.
				state.bySource[key] = [row, ...existing.filter((r) => r._id !== row._id)]
				state.lastGeneratedId = row._id
			})
			.addCase(generateReport.rejected, (state, action) => {
				state.isGenerating = false
				state.error = (action.payload as string) ?? 'Report generation failed'
			})

			.addCase(createReport.fulfilled, (state, action) => {
				const { key, reports } = action.payload
				state.bySource[key] = reports
			})

			.addCase(deleteReport.fulfilled, (state, action) => {
				const { key, reportId } = action.payload
				if (state.bySource[key]) {
					state.bySource[key] = state.bySource[key].filter((r) => r._id !== reportId)
				}
			})
	},
})

export const { clearLastGenerated } = reportSlice.actions

export default reportSlice
